/**
 * TrainingLaunchDisclosureModal (K108-R Batch 1)
 *
 * PER-LAUNCH transparency disclosure. Unlike the durable one-time monitoring
 * consent (TCX-8 `TrainingMonitoringConsentModal`), this appears EVERY time a
 * user opens a training artifact, so the technician is always aware that THIS
 * specific OptiBay-launched session is monitored — never their general browsing.
 *
 * Transparency/accountability, not a legal loophole: there is intentionally NO
 * "don't show again" option. "Agree & continue" proceeds to the existing
 * Companion handoff/launch flow (which stays popup-safe); "Cancel" does not
 * launch. The durable consent gate still runs afterwards unchanged.
 */
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Eye } from 'lucide-react';
import { detectBrowserFamily, type BrowserFamily } from '@/lib/trainingCompanion/companionAvailability';

export interface TrainingLaunchDisclosureModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** "Agree & continue" — proceed to the monitored launch (a fresh user gesture). */
  onAgree: () => void;
  /** "Cancel" / dismiss — do NOT launch. */
  onCancel: () => void;
  /** Override for tests; defaults to UA detection. Drives the Safari-only note. */
  browserFamily?: BrowserFamily;
}

/** Safari-only note: OptiBay consent ≠ the separate one-time Safari website permission. */
export const TRAINING_LAUNCH_DISCLOSURE_SAFARI_NOTE =
  'After you agree, Safari may also ask you to allow website access for OptiBay Training Companion. This is a one-time browser permission required for Companion verification.';

/** What OptiBay MAY track for this launched session (asserted by tests). */
export const TRAINING_LAUNCH_DISCLOSURE_TRACKS = [
  'whether this exact approved training page opens',
  'whether the assigned training video/page remains selected',
  'visible/active time',
  'video progress when available',
  'navigation away from the assigned artifact',
] as const;

/** What OptiBay does NOT monitor (asserted by tests). */
export const TRAINING_LAUNCH_DISCLOSURE_NOT_TRACKED =
  'OptiBay does not monitor unrelated browsing, other YouTube videos, passwords, form fields, clipboard, or general browsing history.';

export function TrainingLaunchDisclosureModal({
  open,
  onOpenChange,
  onAgree,
  onCancel,
  browserFamily,
}: TrainingLaunchDisclosureModalProps) {
  const isSafari = (browserFamily ?? detectBrowserFamily()) === 'safari';
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Closing by any means (X, overlay, Esc) is a cancel — does not launch.
        if (!next) onCancel();
        onOpenChange(next);
      }}
    >
      <DialogContent className="max-w-md" data-testid="training-launch-disclosure-modal">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Eye className="h-5 w-5 text-muted-foreground flex-shrink-0" />
            <DialogTitle>OptiBay will monitor this training artifact</DialogTitle>
          </div>
          <DialogDescription>
            To support training recommendations and completion evidence, OptiBay will monitor
            limited engagement signals for this OptiBay-launched training session only.
          </DialogDescription>
        </DialogHeader>

        <p className="text-sm text-muted-foreground">OptiBay may track:</p>
        <ul className="space-y-2 text-sm text-muted-foreground" data-testid="training-launch-disclosure-tracks">
          {TRAINING_LAUNCH_DISCLOSURE_TRACKS.map((line) => (
            <li key={line} className="flex gap-2">
              <span aria-hidden className="mt-1.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-muted-foreground/60" />
              <span>{line}</span>
            </li>
          ))}
        </ul>

        <p className="text-sm text-foreground" data-testid="training-launch-disclosure-not-tracked">
          {TRAINING_LAUNCH_DISCLOSURE_NOT_TRACKED}
        </p>

        {isSafari && (
          <p
            className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground"
            data-testid="training-launch-disclosure-safari-note"
          >
            {TRAINING_LAUNCH_DISCLOSURE_SAFARI_NOTE}
          </p>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={onCancel} data-testid="training-launch-disclosure-cancel">
            Cancel
          </Button>
          <Button onClick={onAgree} data-testid="training-launch-disclosure-agree">
            Agree &amp; continue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
